import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import { DEFAULT_LABEL_CONFIG } from "app/themes/components/input";
import { AssignedTaskTags } from "components/assigned-tags";
import CircularProgress from "components/circular-progress";
import ColorPickerField from "components/color-picker-field";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import DateStringFormField from "components/date-string-form-field";
import {
  DEFAULT_DATE_QUICK_OPTIONS,
  defaultOnQuickOptionClick
} from "components/date-string-form-field/date-string-form-field.util";
import InputFormField from "components/input-form-field";
import MentionSaveHint from "components/mentions/mention-save-hint.component";
import { MENTION_SOLID_CONTENT_STYLE } from "components/mentions/mentions.util";
import useMentionEditor from "components/mentions/use-mention-editor.hook";
import PatientFieldWithSearch from "components/patient-field-with-search";
import type { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import ToggleButtonGroupFormField from "components/toggle-button-group-form-field/toggle-button-group-form-field.component";
import WordProcessorField from "components/word-processor-field";
import {
  STAFF_TASK_COLORS_FEATURE,
  TASK_TEMPLATE_RICH_TEXT_FEATURE,
  TEAMS_FEATURE,
  TEAM_MENTIONS_FEATURE
} from "constants/feature-flags";
import { TASK_TITLE_MAX_LENGTH } from "constants/staff-tasks";
import { TASK_TEMPLATE_TYPES } from "constants/templates";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { PATIENT_HEADER_QUERY } from "features/patients/use-patient.hook";
import TaskStatusField from "features/staff-tasks-v2/drawer/staff-task-card/task-status-field-component";
import { useStaffTaskRequiredFields } from "features/staff-tasks-v2/hooks/use-staff-task-required-fields.hook";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { useGetPatientQuery } from "services/patients";
import type { StaffTask } from "services/types/staff-tasks.type";

import useTeamAssigneeField from "../../../hooks/use-team-assignee-field.hook";
import {
  EMPTY_DEFAULT_TASK_VALUES,
  type StaffTaskV2FormValues,
  getInitialValues,
  getTaskPriorityOptions,
  validateTaskPriority
} from "../../../staff-task.utils";
import TeamSelectFormField from "../../team-select-form-field.component";
import StaffTaskAttachments, {
  type StaffTaskAttachmentsRef
} from "../staff-task-attachments";

type EditStaffTaskFormProps = {
  task: StaffTask | undefined;
  onSaveCallback?: () => void;
};

export default function EditStaffTaskForm({
  task,
  onSaveCallback
}: EditStaffTaskFormProps) {
  const params = useParams();
  const currentUser = useAppSelector(selectCurrentUser);
  const attachmentsRef = useRef<StaffTaskAttachmentsRef>(null);
  const flags = useFlags();
  const enableTaskRichText = Boolean(flags[TASK_TEMPLATE_RICH_TEXT_FEATURE]);
  // @mentions in the description: the backend notifies the people whose pills are in it on Save.
  const enableMentions = Boolean(flags[TEAM_MENTIONS_FEATURE]);
  // Teams: "Send to a team" beside the assignee. A team task needs no assignee (it is Unclaimed).
  const enableTeams = Boolean(flags[TEAMS_FEATURE]);
  const currentClinicId = useAppSelector(selectCurrentClinic)?.id;
  const mentionEditorProps = useMentionEditor({
    enabled: enableMentions,
    clinicId: currentClinicId,
    contentStyle: MENTION_SOLID_CONTENT_STYLE,
    // A team pill in the description reaches every member on save (TEAMS_FEATURE).
    includeTeams: enableMentions && enableTeams
  });

  const {
    isPatientViewMode,
    taskModalMode,
    patientId,
    handleUpdate,
    handleCreate,
    isUpdating,
    handleCloseTaskModal,
    handleOpenTaskModal
  } = useStaffTasksV2();

  const isNew = taskModalMode.displayMode === "create";

  const { isDueDateRequired, isPriorityRequired } =
    useStaffTaskRequiredFields();
  const priorityOptions = useMemo(
    () => getTaskPriorityOptions(isPriorityRequired),
    [isPriorityRequired]
  );

  const patientIdParams = Number(
    (params.patientId || String(params["*"] ?? "").split("/")[0]) ?? ""
  );

  const fetchPatientId = isNew ? patientId || patientIdParams : patientId;

  const { data: initialPatient } = useGetPatientQuery(
    { patientId: Number(fetchPatientId), query: PATIENT_HEADER_QUERY },
    {
      skip: !fetchPatientId || fetchPatientId === -1
    }
  );

  const initialValues: StaffTaskV2FormValues = useMemo(
    () =>
      getInitialValues(task, currentUser, enableTaskRichText, enableMentions),
    [task, currentUser, enableTaskRichText, enableMentions]
  );

  const defaultValues: StaffTaskV2FormValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_TASK_VALUES, initialValues);
  }, [initialValues]);

  const formMethods = useForm<StaffTaskV2FormValues>({
    mode: "onBlur",
    defaultValues
  });

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { isValid, errors }
  } = formMethods;

  const assignee = useWatch({ control, name: "assignee" });
  // Teams: a person OR a team (the rule reads the team when it runs; see the hook).
  const teamAssignee = useTeamAssigneeField({ form: formMethods, enableTeams });
  const color = useWatch({ control, name: "color" });

  const [status, patientField]: [StatusTransitionCode | undefined, any] = watch<
    ["status", "patient"]
  >(["status", "patient"]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset, isNew]);

  useEffect(() => {
    if (isNew) {
      setValue("patient", initialPatient);
    }
  }, [setValue, initialPatient, taskModalMode.displayMode, isNew]);

  const handleStatusChange = useCallback(
    (code: StatusTransitionCode) => {
      setValue("status", code);
      return new Promise((resolve, reject) => {
        resolve(code);
      });
    },
    [setValue]
  );

  const onSubmit = useCallback(
    (data: StaffTaskV2FormValues) => {
      if (data.id) {
        handleUpdate(data, onSaveCallback);
        if (taskModalMode.onEditCancelMode === "openViewModal") {
          handleOpenTaskModal({ mode: "view", taskId: data.id });
        } else {
          handleCloseTaskModal();
        }
      } else {
        handleCreate(data, async (createdTaskId) => {
          if (createdTaskId && attachmentsRef.current?.hasPendingFiles()) {
            await attachmentsRef.current.uploadPendingFiles(createdTaskId);
          }
        });
        handleCloseTaskModal();
      }
    },
    [
      handleCloseTaskModal,
      handleCreate,
      handleOpenTaskModal,
      handleUpdate,
      onSaveCallback,
      taskModalMode.onEditCancelMode
    ]
  );

  if (taskModalMode.displayMode === "edit" && !task) {
    return <CircularProgress sx={{ width: "100%" }} />;
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        <Grid item xs={4}>
          <Stack rowGap={2}>
            <ToggleButtonGroupFormField
              name="priority"
              label="Priority"
              options={priorityOptions}
              control={control}
              required
              errors={errors}
              rules={{ validate: validateTaskPriority(isPriorityRequired) }}
            />
            <DateStringFormField
              name="due_at"
              label="Due Date"
              required={isDueDateRequired}
              control={control}
              PopperProps={{
                style: { zIndex: 14001 }
              }}
              quickOptions={DEFAULT_DATE_QUICK_OPTIONS}
              onQuickOptionClick={defaultOnQuickOptionClick}
              sx={{
                ml: 0,
                pr: 0,
                minWidth: "100%",
                "& > div": { flexGrow: 1 }
              }}
            />
            <Stack rowGap={0.8}>
              <FormLabel sx={DEFAULT_LABEL_CONFIG} id="status">
                Status *
              </FormLabel>
              <TaskStatusField value={status} onChange={handleStatusChange} />
            </Stack>

            {flags[STAFF_TASK_COLORS_FEATURE] && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <ColorPickerField
                  control={control}
                  label="Task Color"
                  zIndex={14001}
                />
                {color && (
                  <Button
                    size="small"
                    onClick={() => setValue("color", null)}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            )}

            {task && <AssignedTaskTags task={task} readonly />}
          </Stack>
        </Grid>
        <Grid item xs={8} sx={{ boxSizing: "border-box", pl: 5 }}>
          <Stack rowGap={2}>
            <InputFormField
              name="title"
              label="Title"
              control={control}
              required
              autoFocus
              inputProps={{ maxLength: TASK_TITLE_MAX_LENGTH }}
              characterLimit={TASK_TITLE_MAX_LENGTH}
              sx={{ ml: 0 }}
            />
            {task?.implementation ? (
              // Onboarding-hub tasks: assignment is Suno-managed and the server silently
              // ignores clinic writes to assignee/assignees — editable pickers here would
              // "save" and change nothing. Show who's on it instead.
              <Stack rowGap={0.5} sx={{ ml: 0 }}>
                <FormLabel>Assignee</FormLabel>
                <Typography variant="body1">
                  {[
                    task.assignee?.full_name,
                    ...(task.assignees || []).map(({ user }) => user?.full_name)
                  ]
                    .filter(Boolean)
                    .join(", ") || "Unassigned"}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Assignment on onboarding tasks is managed by your Suno
                  implementation team.
                </Typography>
              </Stack>
            ) : (
              <>
                <SelectProvider
                  isActive={true}
                  name="assignee"
                  label={enableTeams ? "Assign to a person" : "Assignee"}
                  includeOtherClinics
                  ignoreRegionsFilter
                  ignoreClinicsFilter
                  control={control}
                  required={teamAssignee.required}
                  rules={teamAssignee.rules}
                  sx={{ ml: 0 }}
                />
                {teamAssignee.hint && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: -1 }}
                    data-testid="team-assignee-hint"
                  >
                    {teamAssignee.hint}
                  </Typography>
                )}
                {enableTeams && (
                  <TeamSelectFormField
                    name="team"
                    control={control}
                    // A new team: nobody owns it until a member takes it. Re-picking the team the task
                    // already has keeps whoever is on it (the team still follows the task).
                    onOptionSelected={(picked) =>
                      teamAssignee.onTeamSelected(picked, {
                        clearPerson:
                          Boolean(picked) &&
                          picked?.value !== task?.team?.id?.toString()
                      })
                    }
                  />
                )}
                <SelectProvider
                  isActive
                  name="assignees"
                  label="Additional Assignees"
                  includeOtherClinics
                  ignoreRegionsFilter
                  ignoreClinicsFilter
                  control={control}
                  multiple
                  enableSelectAll
                  excludeSelectAllValues={assignee?.value}
                  sx={{ ml: 0 }}
                />
              </>
            )}
            <PatientFieldWithSearch
              label="Patient"
              control={control}
              required={isPatientViewMode}
            />
          </Stack>
        </Grid>
      </Grid>
      <Box width="100%" sx={{ mt: 2 }}>
        <WordProcessorField
          name="description"
          label="Description"
          control={control}
          placeholder="Enter task details"
          height={210}
          featuresGroup={enableTaskRichText ? "default" : "textOnly"}
          allowTemplates
          allowMergeTags
          allowInsertMergeTagsValues
          templateTypes={TASK_TEMPLATE_TYPES}
          allowToolbarAutohide={false}
          allowPasteAsText={false}
          mergeTagsPatientId={
            Number(patientField?.value) || initialPatient?.id || -1
          }
          {...mentionEditorProps}
        />
        {enableMentions && <MentionSaveHint />}
      </Box>
      <Box sx={{ mt: 2 }}>
        <StaffTaskAttachments
          ref={attachmentsRef}
          taskId={task?.id}
          attachments={task?.attachments}
          allowExtendedFileTypes={Boolean(task?.implementation)}
        />
      </Box>
      <Stack
        sx={{ mt: 2, pr: 3 }}
        justifyContent="flex-end"
        spacing={2}
        direction="row"
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            if (
              task?.id &&
              taskModalMode.onEditCancelMode === "openViewModal"
            ) {
              handleOpenTaskModal({ mode: "view", taskId: task.id });
            } else {
              handleCloseTaskModal();
            }
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          disabled={!isValid}
          loading={isUpdating}
          variant="contained"
          size="small"
          type="submit"
        >
          Save
        </LoadingButton>
      </Stack>
    </Box>
  );
}
